import { useNavigate } from 'react-router-dom';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Badge, Card, DataTable, Pagination, Select } from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { humanise, relative } from '../../lib/format.js';
import { severityTone } from '../../lib/status.js';

/** What a row says it is about: the listing's title, or who was talking. */
function targetLabel(target = {}) {
  if (target.missing) return 'Since removed';
  if (target.type === 'broker_property') return `Listing: ${target.title}`;
  if (target.type === 'conversation') {
    const names = (target.participants || []).map((person) => person.name || 'Unnamed');
    return `Chat: ${names.join(' and ')}`;
  }
  return humanise(target.type);
}

/**
 * What people have reported — listings from "Report this listing", and chats
 * from the report button in a conversation.
 *
 * Open reports first, worst first. A row opens the report, where the listing
 * or the conversation is on the same page as the decision.
 */
export default function ReportsPage() {
  const navigate = useNavigate();

  const list = useListQuery({
    key: 'admin-reports',
    fetcher: api.moderation.reports,
    initialFilters: { status: '', targetType: '' },
  });

  const columns = [
    {
      key: 'target',
      header: 'Reported',
      render: (report) => (
        <div>
          <div className="table__primary">{targetLabel(report.target)}</div>
          <div className="table__muted">
            {report.target?.accused?.name ? `Against ${report.target.accused.name}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Reason',
      render: (report) => (
        <div>
          <div>{humanise(report.category)}</div>
          {report.description ? <div className="table__muted">{report.description.slice(0, 80)}</div> : null}
        </div>
      ),
    },
    {
      key: 'reporter',
      header: 'Reported by',
      render: (report) => report.reporter?.name || <span className="table__muted">Unknown</span>,
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (report) => <Badge tone={severityTone(report.severity)} label={report.severity} />,
    },
    { key: 'status', header: 'Status', render: (report) => <Badge status={report.status} /> },
    { key: 'when', header: 'When', render: (report) => <span className="table__muted">{relative(report.createdAt)}</span> },
  ];

  return (
    <Page title="Reports" subtitle="Listings and chats people have reported. Open ones first, most severe first.">
      <Card bodyless>
        <div className="card__body">
          <div className="toolbar">
            <Select
              value={list.filters.status}
              onChange={(event) => list.setFilter('status', event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Open</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </Select>
            <Select
              value={list.filters.targetType}
              onChange={(event) => list.setFilter('targetType', event.target.value)}
              aria-label="Filter by what was reported"
            >
              <option value="">Listings and chats</option>
              <option value="broker_property">Listings</option>
              <option value="conversation">Chats</option>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={list.rows}
          query={list.query}
          onRowClick={(report) => navigate(`/reports/${report.id}`)}
          empty={{
            title: 'Nothing to review',
            body: 'No reports match these filters. New ones arrive when someone reports a listing or a chat in the apps.',
          }}
        />
        <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
      </Card>
    </Page>
  );
}
